import { Check, Search, Video, Sparkles } from 'lucide-react'
import { cn } from '@/lib/utils'
import { SlideIn } from '@/components/motion'

type ReviewStatus = 'draft' | 'submitted' | 'changes_requested' | 'approved' | 'rejected' | null

interface WhatsNextTimelineProps {
  reviewStatus: ReviewStatus
  isProfileComplete: boolean
}

interface TimelineStep {
  id: string
  title: string
  description: string
  icon: React.ElementType
  status: 'completed' | 'current' | 'upcoming'
}

function getTimelineSteps(reviewStatus: ReviewStatus, isProfileComplete: boolean): TimelineStep[] {
  // Determine step statuses based on review status and profile completion
  const isSubmitted = reviewStatus === 'submitted' || reviewStatus === 'approved'
  const isApproved = reviewStatus === 'approved'

  return [
    {
      id: 'submitted',
      title: 'Profile Submitted',
      description: 'Your profile and training completion have been successfully submitted for review.',
      icon: Check,
      status: isSubmitted ? 'completed' : (isProfileComplete ? 'current' : 'upcoming'),
    },
    {
      id: 'verification',
      title: 'Expert Verification',
      description: 'Our team is reviewing your credentials, experience, and training completion. Expected: 1-2 business days.',
      icon: Search,
      status: isApproved ? 'completed' : (isSubmitted ? 'current' : 'upcoming'),
    },
    {
      id: 'interview',
      title: 'Interview',
      description: "We'll schedule a 30-45 minute interview to discuss your expertise and answer any questions you have.",
      icon: Video,
      status: 'upcoming', // Always upcoming until approved
    },
    {
      id: 'matching',
      title: 'Start Matching',
      description: "Once approved, you'll be added to our expert network and start receiving client project opportunities!",
      icon: Sparkles,
      status: isApproved ? 'completed' : 'upcoming',
    },
  ]
}

export function WhatsNextTimeline({ reviewStatus, isProfileComplete }: WhatsNextTimelineProps) {
  // Don't show if approved
  if (reviewStatus === 'approved') {
    return null
  }

  const steps = getTimelineSteps(reviewStatus, isProfileComplete)

  return (
    <SlideIn direction="up" delay={0.5}>
      <div className="bg-white rounded-2xl sm:rounded-3xl lg:rounded-[40px] border border-slate-200 p-6 sm:p-8">
        <h2 className="text-2xl sm:text-[28px] font-normal text-slate-900 mb-6 sm:mb-8">
          What's Next?
        </h2>

        <div className="space-y-0">
          {steps.map((step, index) => {
            const Icon = step.icon
            const isLast = index === steps.length - 1

            return (
              <div key={step.id} className="relative flex gap-4">
                {/* Timeline line */}
                {!isLast && (
                  <div
                    className={cn(
                      "absolute left-[23px] top-12 w-0.5 h-[calc(100%-24px)]",
                      step.status === 'completed' ? 'bg-emerald-500' : 'bg-slate-200'
                    )}
                  />
                )}

                {/* Icon */}
                <div
                  className={cn(
                    "relative z-10 w-12 h-12 rounded-full flex items-center justify-center flex-shrink-0 border-2",
                    step.status === 'completed'
                      ? 'bg-emerald-500 border-emerald-500'
                      : step.status === 'current'
                        ? 'bg-[#4669ed] border-[#4669ed]'
                        : 'bg-white border-slate-200'
                  )}
                >
                  <Icon
                    className={cn(
                      "w-5 h-5",
                      step.status === 'completed' || step.status === 'current'
                        ? 'text-white'
                        : 'text-slate-400'
                    )}
                  />
                </div>

                {/* Content */}
                <div className={cn("flex-1 pb-6", isLast && "pb-0")}>
                  <h3
                    className={cn(
                      "text-lg font-semibold mb-1",
                      step.status === 'completed'
                        ? 'text-emerald-700'
                        : step.status === 'current'
                          ? 'text-slate-900'
                          : 'text-slate-500'
                    )}
                  >
                    {step.title}
                    {step.status === 'current' && (
                      <span className="ml-2 text-sm font-normal text-[#4669ed]">(Current)</span>
                    )}
                  </h3>
                  <p
                    className={cn(
                      "text-sm leading-relaxed",
                      step.status === 'upcoming' ? 'text-slate-400' : 'text-slate-600'
                    )}
                  >
                    {step.description}
                  </p>
                </div>
              </div>
            )
          })}
        </div>
      </div>
    </SlideIn>
  )
}
